import React, { createContext, useContext, useState, useEffect } from 'react';
import supabaseService from '../services/supabase';
import firebaseMetadataService from '../services/firebaseMetadataService';
import hybridImageService from '../services/hybridImageService';

const ImageContext = createContext();

export const useImages = () => {
  const context = useContext(ImageContext);
  if (context === undefined) {
    throw new Error('useImages must be used within an ImageProvider');
  }
  return context;
};

export const ImageProvider = ({ children }) => {
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Load images from both Supabase and Vercel Blob with hybrid system
  const loadImages = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      console.log('🖼️ Loading images from hybrid storage (Supabase + Vercel Blob)...');

      // Load images from both storage providers
      const [supabaseImages, vercelBlobImages] = await Promise.allSettled([
        loadSupabaseImages(),
        loadVercelBlobImages()
      ]);

      let allImages = [];

      // Process Supabase images
      if (supabaseImages.status === 'fulfilled' && supabaseImages.value) {
        allImages = [...allImages, ...supabaseImages.value];
        console.log(`✅ Loaded ${supabaseImages.value.length} images from Supabase`);
      } else {
        console.warn('⚠️ Failed to load Supabase images:', supabaseImages.reason);
      }

      // Process Vercel Blob images
      if (vercelBlobImages.status === 'fulfilled' && vercelBlobImages.value) {
        allImages = [...allImages, ...vercelBlobImages.value];
        console.log(`✅ Loaded ${vercelBlobImages.value.length} images from Vercel Blob`);
      } else {
        console.warn('⚠️ Failed to load Vercel Blob images:', vercelBlobImages.reason);
      }

      // Remove duplicates based on filename
      const uniqueImages = allImages.reduce((acc, current) => {
        const existing = acc.find(item => item.name === current.name);
        if (!existing) {
          acc.push(current);
        } else {
          // Prefer Vercel Blob over Supabase if both exist
          if (current.storage === 'vercel-blob' && existing.storage === 'supabase') {
            const index = acc.findIndex(item => item.name === current.name);
            acc[index] = current;
          }
        }
        return acc;
      }, []);

      setImages(uniqueImages);
      setLastUpdated(new Date());
      console.log(`✅ Total loaded: ${uniqueImages.length} unique images from hybrid storage`);

    } catch (error) {
      console.error('❌ Error loading hybrid images:', error);
      setError(error.message);
      setImages([]);
    } finally {
      setLoading(false);
    }
  };

  // Load images from Supabase
  const loadSupabaseImages = async () => {
    try {
      const imageList = await supabaseService.listImages('products');
      const imagesWithMetadata = [];

      if (imageList && imageList.length > 0) {
        for (const image of imageList) {
          const publicUrl = supabaseService.getPublicUrl(`products/${image.name}`);

          // Try to load metadata from Firebase
          let metadata = null;
          try {
            metadata = await firebaseMetadataService.getImageMetadata(image.name);
          } catch (error) {
            console.log(`No Firebase metadata for ${image.name}`);
          }

          // ONLY include images with admin-added metadata
          if (metadata && metadata.title && metadata.title.trim()) {
            const imageWithMetadata = {
              ...image,
              id: image.name.replace(/\.[^/.]+$/, ""), // Remove file extension for ID
              publicUrl,
              url: publicUrl, // Add url property for compatibility
              fullPath: `products/${image.name}`,
              title: metadata.title,
              description: metadata.description || '',
              category: metadata.category || '',
              price: metadata.price || null,
              inStock: metadata.in_stock !== false,
              featured: metadata.featured || false,
              tags: metadata.tags || [],
              specifications: metadata.specifications || {},
              hasMetadata: true,
              storage: 'supabase',
              isUsed: false // Will be updated when products are loaded
            };

            imagesWithMetadata.push(imageWithMetadata);
          }
        }
      }

      return imagesWithMetadata;
    } catch (error) {
      console.error('Error loading Supabase images:', error);
      return [];
    }
  };

  // Load images from Vercel Blob
  const loadVercelBlobImages = async () => {
    try {
      const blobImages = await hybridImageService.getImagesFromVercelBlob();
      return blobImages.map(image => ({
        ...image,
        storage: 'vercel-blob',
        publicUrl: image.url,
        fullPath: image.pathname,
        isUsed: false
      }));
    } catch (error) {
      console.error('Error loading Vercel Blob images:', error);
      return [];
    }
  };

  // NO automatic data generation - all metadata comes from Firebase admin entries

  // Get images by category
  const getImagesByCategory = (category) => {
    if (category === 'All') return images;
    return images.filter(image => image.category === category);
  };

  // Get random image from category
  const getRandomImageFromCategory = (category) => {
    const categoryImages = getImagesByCategory(category);
    if (categoryImages.length === 0) return null;
    
    const randomIndex = Math.floor(Math.random() * categoryImages.length);
    return categoryImages[randomIndex];
  };

  // Get featured images (first few from each category)
  const getFeaturedImages = () => {
    const categories = ['Microcontrollers', 'WiFi Modules', 'Sensors', 'Components'];
    const featuredImages = [];
    
    categories.forEach(category => {
      const categoryImages = getImagesByCategory(category);
      if (categoryImages.length > 0) {
        featuredImages.push(categoryImages[0]); // Take first image from each category
      }
    });
    
    return featuredImages;
  };

  // Get unique categories
  const getCategories = () => {
    const categories = [...new Set(images.map(image => image.category))];
    return ['All', ...categories];
  };

  // Upload new image
  const uploadImage = async (file, folder = 'products') => {
    try {
      console.log('📤 Uploading image to Supabase storage...');
      
      const uploadedImage = await supabaseService.uploadImage(file, folder);
      
      // Reload images to include the new one
      await loadImages(true);
      
      console.log('✅ Image uploaded successfully:', uploadedImage);
      return uploadedImage;
    } catch (error) {
      console.error('❌ Error uploading image:', error);
      throw error;
    }
  };

  // Delete image
  const deleteImage = async (imagePath) => {
    try {
      console.log('🗑️ Deleting image from Supabase storage...');
      
      await supabaseService.deleteImage(imagePath);
      
      // Remove from local state
      setImages(prev => prev.filter(image => image.fullPath !== imagePath));
      
      console.log('✅ Image deleted successfully:', imagePath);
      return true;
    } catch (error) {
      console.error('❌ Error deleting image:', error);
      throw error;
    }
  };

  // Update image metadata in Firebase
  const updateImageMetadata = async (imageName, metadata) => {
    try {
      console.log('📝 Updating image metadata in Firebase...');

      // Save to Firebase
      await firebaseMetadataService.saveImageMetadata(imageName, metadata);
      console.log('✅ Metadata saved to Firebase');

      // Update local state for immediate UI feedback
      setImages(prev => prev.map(image =>
        image.name === imageName
          ? { ...image, ...metadata, hasMetadata: true }
          : image
      ));

      console.log('✅ Image metadata updated successfully:', imageName);
      return true;
    } catch (error) {
      console.error('❌ Error updating image metadata:', error);
      throw error;
    }
  };

  // Refresh images
  const refreshImages = () => {
    return loadImages(true);
  };

  // Load images on mount
  useEffect(() => {
    loadImages();
  }, []);

  // Listen for image updates
  useEffect(() => {
    const handleImageUpdate = () => {
      console.log('🔄 Image update event received, refreshing images...');
      setTimeout(() => {
        loadImages(true);
      }, 500);
    };

    window.addEventListener('imageDataUpdated', handleImageUpdate);

    return () => {
      window.removeEventListener('imageDataUpdated', handleImageUpdate);
    };
  }, []);

  const value = {
    // State
    images,
    loading,
    error,
    lastUpdated,

    // Actions
    loadImages,
    uploadImage,
    deleteImage,
    updateImageMetadata,
    refreshImages,

    // Getters
    getImagesByCategory,
    getRandomImageFromCategory,
    getFeaturedImages,
    getCategories,

    // Computed values
    totalImages: images.length,
    categories: getCategories(),
    featuredImages: getFeaturedImages()
  };

  return (
    <ImageContext.Provider value={value}>
      {children}
    </ImageContext.Provider>
  );
};

export default ImageContext;
